import { useState, ChangeEvent, useCallback } from "react"

const integerRegex = /^-?[0-9]+$/
const decimalRegex = /^-?[0-9]+\.?[0-9]*|-$/

interface UseHtmlInputData {
    value: number | undefined
    isValid: boolean
    onChange: (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void
    resetValidity: () => void
}

export function useNumberInput(
    initialValue: number | undefined,
    initialValid: boolean = true,
    additionalProps?: { type: "integer" | "float"; max?: number; min?: number }
): UseHtmlInputData {
    const [value, setValue] = useState(initialValue)
    const [isValid, setValid] = useState(initialValid)
    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const inputValue = event.target.value
            const sanitizedValue =
                additionalProps === undefined
                    ? [inputValue]
                    : additionalProps.type === "float"
                    ? inputValue.match(decimalRegex)
                    : inputValue.match(integerRegex)

            const newValue = sanitizedValue ? Number(sanitizedValue[0]) : 0
            const isValidValue =
                newValue <= (additionalProps?.max ?? Infinity) &&
                newValue >= (additionalProps?.min ?? -Infinity)

            setValue(
                isValidValue
                    ? newValue
                    : value !== undefined &&
                      value > (additionalProps?.max ?? Infinity)
                    ? additionalProps?.max ?? value
                    : value
            )

            setValid(isValidValue && event.target.validity.valid)
        },
        [additionalProps, value]
    )

    const handleResetValidity = useCallback(() => setValid(true), [])

    return {
        value,
        isValid,
        onChange: handleChange,
        resetValidity: handleResetValidity
    }
}
